import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NC_COUNTIES } from "@/lib/ncCounties";
import { getCountyPref, setCountyPref } from "@/lib/countyPref";
import { MapPin, Search, Check, X } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { pageText } from "@/lib/i18n/pages";

export default function CountyGate() {
  const { lang } = useI18n();
  const es = lang === "es";
  const g = pageText(lang, "gate");
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("select"); // select | confirm | autofill
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const path = window.location.pathname;
    if (/^\/(login|register|admin|auth|firm|join|featured)/.test(path)) return; // don't interrupt auth/admin/firm login
    const pref = getCountyPref();
    if (!pref || !pref.dismissed) {
      const t = setTimeout(() => setOpen(true), 700);
      return () => clearTimeout(t);
    }
  }, []);

  useEffect(() => {
    const reopen = () => { setStep("select"); setQuery(""); setSelected(null); setOpen(true); };
    window.addEventListener("open-county-gate", reopen);
    return () => window.removeEventListener("open-county-gate", reopen);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return NC_COUNTIES;
    return NC_COUNTIES.filter((c) => c.name.toLowerCase().includes(q) || (c.seat || "").toLowerCase().includes(q));
  }, [query]);

  const skip = () => { setCountyPref({ dismissed: true }); setOpen(false); };
  const closeGate = () => {
    const pref = getCountyPref();
    if (pref?.dismissed) setOpen(false); // already set up — just close, keep prefs
    else skip();
  };
  const finish = (autofill) => {
    setCountyPref({ county: selected.name, slug: selected.slug, autofill, dismissed: true });
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) closeGate(); }}>
      <DialogContent className="rounded-sm max-w-md" data-testid="county-gate">
        {step === "select" && (
          <>
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl flex items-center gap-2"><MapPin size={20} className="text-accent" /> {g?.title || (es ? "Elija su condado" : "Choose your county")}</DialogTitle>
              <DialogDescription>{g?.desc || (es ? "Elija su condado de Carolina del Norte para personalizar la información y agilizar sus formularios." : "Pick your North Carolina county to personalize filing info and speed up your forms.")}</DialogDescription>
            </DialogHeader>
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input data-testid="county-search" autoFocus value={query} onChange={(e) => setQuery(e.target.value)} placeholder={g?.search || (es ? "Buscar entre los 100 condados…" : "Search all 100 counties…")} className="rounded-sm pl-9" />
            </div>
            <div className="max-h-72 overflow-y-auto border border-border rounded-sm" data-testid="county-list">
              {filtered.map((c) => (
                <button key={c.slug} data-testid={`county-opt-${c.slug}`} onClick={() => { setSelected(c); setStep("confirm"); }}
                  className="w-full flex items-center justify-between px-4 py-2.5 text-left text-sm border-b border-border/60 hover:bg-secondary transition-colors">
                  <span className="font-medium">{g ? g.county.replace("{name}", c.name).replace(/\bde ([AEIOU])/, lang === "fr" ? "d'$1" : "de $1") : es ? `Condado de ${c.name}` : `${c.name} County`}</span>
                  <span className="text-xs text-muted-foreground">{c.seat}</span>
                </button>
              ))}
              {filtered.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">{g?.none || (es ? "No se encontró el condado" : "No county found")}</p>}
            </div>
            <button onClick={skip} data-testid="county-skip" className="text-xs text-muted-foreground hover:text-foreground mx-auto mt-1">{g?.skip || (es ? "Omitir por ahora" : "Skip for now")}</button>
          </>
        )}

        {step === "confirm" && selected && (
          <>
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl">Set your default county?</DialogTitle>
              <DialogDescription>Is <span className="font-semibold text-foreground">{selected.name} County</span> your default county?</DialogDescription>
            </DialogHeader>
            <div className="flex flex-col sm:flex-row gap-3 mt-2">
              <Button onClick={() => setStep("autofill")} data-testid="county-confirm-yes" className="flex-1 rounded-sm gap-2 bg-primary hover:bg-accent hover:text-accent-foreground transition-colors">
                <Check size={16} /> Yes, that's my county
              </Button>
              <Button onClick={() => { setSelected(null); setStep("select"); }} data-testid="county-confirm-no" variant="outline" className="flex-1 rounded-sm gap-2 hover:bg-secondary">
                <X size={16} /> Choose another
              </Button>
            </div>
          </>
        )}

        {step === "autofill" && selected && (
          <>
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl">Auto-fill your county?</DialogTitle>
              <DialogDescription>Should <span className="font-semibold text-foreground">{selected.name} County</span> be automatically filled in on all your forms?</DialogDescription>
            </DialogHeader>
            <div className="flex flex-col sm:flex-row gap-3 mt-2">
              <Button onClick={() => finish(true)} data-testid="county-autofill-yes" className="flex-1 rounded-sm gap-2 bg-accent text-accent-foreground hover:bg-primary hover:text-primary-foreground transition-colors">
                <Check size={16} /> Yes, auto-fill it
              </Button>
              <Button onClick={() => finish(false)} data-testid="county-autofill-no" variant="outline" className="flex-1 rounded-sm gap-2 hover:bg-secondary">
                No thanks
              </Button>
            </div>
            <p className="text-[0.7rem] text-muted-foreground text-center mt-1">You can always change the county on any individual form.</p>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
